var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) {
        if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
    }

    return t;
  };

  return __assign.apply(this, arguments);
};

var __rest = this && this.__rest || function (s, e) {
  var t = {};

  for (var p in s) {
    if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  }

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
};
/**
 * Lunchbox Web Ordering's Dynamic Factory v1
 * The Dynamic Factory is responsible for parsing structure and render data
 *
 * It is recommended to import this component as a default export for best performance.
 * Use named export for the default structure or typescript definitions.
 *
 * Here is a quick example:
 * import DynamicFactory, { DynamicStructure, View, Label, Button, ...etc } from "@lunchboxinc/dynamic-web/DynamicFactory";
 */


import * as React from "react";
import { VisuallyHidden } from "@react-aria/visually-hidden";
import { useHover, usePress } from "@react-aria/interactions";
import { mergeProps } from "@react-aria/utils";
import chroma from "chroma-js";
import merge from "../helpers/merge";
import { prefixWith$ } from "./helpers";
import { ButtonComponent, CollectionViewComponent, InputComponent, LabelComponent, ViewComponent, LinearLayoutComponent } from "./styled";
import { StructureContext, useStructureContext, NodeStateContext } from "./context";
var isDevelopment = process.env.NODE_ENV === "development";
/**
 * Manages the state of a stateful node.
 * Returns the state value and a set of event listeners
 * to be spread on that component.
 */

var useStatefulNode = function useStatefulNode(props) {
  var _a, _b;

  var _c = useHover({}),
      hoverProps = _c.hoverProps,
      isHovered = _c.isHovered;

  var _d = usePress({}),
      pressProps = _d.pressProps,
      isPressed = _d.isPressed;

  var state = "unselected"; // If there's only one state, don't attach props.
  // Otherwise, we introduce more rerenders.

  if (!props.states || Object.keys(props.states).length === 1) return {
    state: state
  };
  if (((_a = props.states) === null || _a === void 0 ? void 0 : _a.hover) && isHovered && !isPressed) state = "hover";
  if (((_b = props.states) === null || _b === void 0 ? void 0 : _b.selected) && ((props === null || props === void 0 ? void 0 : props.selected) || isPressed)) state = "selected";
  if (props.disabled) state = "disabled";

  var elementProps = __assign(__assign({}, pressProps), hoverProps);

  return {
    state: state,
    elementProps: elementProps
  };
};
/**
 * Renders a view based on the current stucture context.
 */


function View(_a) {
  var _b, _c;

  var viewRef = _a.viewRef,
      props = __rest(_a, ["viewRef"]);

  var structure = useStructureContext().structure;
  var statefulNodeProps = useStatefulNode(props);
  var inner = null;

  if (props.linearLayout) {
    var linearLayoutProps = structure.linearLayouts[props.linearLayout];

    if (!linearLayoutProps) {
      console.error("lbx/dynamic-web", "Insufficient linear layout data was found for the ".concat(props.linearLayout, " view."));
    } else {
      inner = React.createElement(LinearLayout, __assign({}, linearLayoutProps, {
        name: props.linearLayout
      }), linearLayoutProps.children);
    }
  }

  var $styles = __assign(__assign({}, (_b = props.states) === null || _b === void 0 ? void 0 : _b.unselected), (_c = props.states) === null || _c === void 0 ? void 0 : _c[statefulNodeProps.state]); // FIXME: This needs to implement the spec for media rendering.


  var style = (props === null || props === void 0 ? void 0 : props.media) ? __assign(__assign({}, props.style), {
    backgroundImage: "url(\"".concat(props === null || props === void 0 ? void 0 : props.media, "\")"),
    backgroundSize: "contain",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat"
  }) : __assign({}, props.style);
  return React.createElement(NodeStateContext.Provider, {
    value: statefulNodeProps.state
  }, React.createElement(ViewComponent, __assign({}, isDevelopment && {
    "data-view": "".concat(String(props.name))
  }, statefulNodeProps.elementProps, props, prefixWith$(props), {
    "$styles": $styles,
    style: style,
    ref: viewRef,
    isSeparator: props.name.toLowerCase().includes("_seperator")
  }), inner));
}

View.defaultProps = {
  viewRef: undefined
};
/** Maps node types to the default renderer. */

var renderDict = {
  buttons: Button,
  collectionViews: CollectionView,
  inputs: Input,
  labels: Label,
  linearLayouts: LinearLayout,
  views: View
};
/**
 * Renders a linear layout based
 */

function LinearLayout(props) {
  var name = props.name,
      children = props.children;

  var _a = useStructureContext(),
      structure = _a.structure,
      lookup = _a.lookup,
      renderers = _a.renderers; // Check if children is a set of nodes keys.


  var inner = children === null || children === void 0 ? void 0 : children.map(function (child) {
    var type = lookup[child];
    var renderer = renderers[child] || renderDict[type];
    var properties = structure[type][child];
    return React.createElement(React.Fragment, {
      key: "".concat(child, "-").concat(Math.floor(Math.random() * 1000 + 1))
    }, typeof renderer === "function" && renderer(__assign(__assign({}, properties), {
      name: child
    })));
  });
  return React.createElement(LinearLayoutComponent, __assign({}, isDevelopment && {
    "data-linearlayout": "".concat(String(name))
  }, prefixWith$(props)), inner);
}
/**
 * Renders a collection view based on the
 * current structure context.
 */


function CollectionView(props) {
  var collection = props.collection,
      loader = props.loader,
      emptyView = props.emptyView,
      renderer = props.renderer,
      name = props.name;
  var structure = useStructureContext().structure; // If the collection is loading, display the loader.

  if (loader) {
    return loader;
  }

  var inner;

  if (!(collection === null || collection === void 0 ? void 0 : collection.length) && emptyView) {
    var emptyViewProps = structure.views[emptyView];
    inner = React.createElement(View, __assign({
      name: emptyView,
      key: 0
    }, emptyViewProps));
  } else {
    inner = collection.map(function (item) {
      return React.createElement(React.Fragment, {
        key: "collectionItem-".concat(Math.floor(Math.random() * 1000 + 1))
      }, renderer(item));
    });
  }

  return React.createElement(CollectionViewComponent, __assign({}, isDevelopment && {
    "data-collectionview": "".concat(String(name))
  }, prefixWith$(props)), inner);
}
/**
 * Renders a button given the structure context.
 */


function Button(_a) {
  var _b, _c, _d;

  var ariaLabel = _a.ariaLabel,
      sx = _a.sx,
      buttonRef = _a.buttonRef,
      props = __rest(_a, ["ariaLabel", "sx", "buttonRef"]);

  var _e = useStatefulNode(props),
      state = _e.state,
      elementProps = _e.elementProps;

  var structure = useStructureContext().structure;
  var linearLayout = props.linearLayout;

  var _f = merge({}, (_b = props.states) === null || _b === void 0 ? void 0 : _b.unselected, (_c = props.states) === null || _c === void 0 ? void 0 : _c[state]),
      styles = _f.styles,
      media = _f.media,
      label = _f.label;

  var node = null;

  if (label) {
    node = React.createElement(Label, {
      name: label,
      as: "span"
    });
  }

  if (linearLayout) {
    var linearLayoutProps = structure.linearLayouts[linearLayout];
    var linearLayoutChildren = (_d = linearLayoutProps === null || linearLayoutProps === void 0 ? void 0 : linearLayoutProps.children) === null || _d === void 0 ? void 0 : _d.map(function (name) {
      if (name === "media") return media;
      if (name === "label") return label;
      return null;
    }).filter(function (i) {
      return i !== null;
    });
    node = React.createElement(LinearLayout, __assign({
      name: linearLayout
    }, linearLayoutProps), linearLayoutChildren);
  }

  if (media) {
    var mediaViewProps = structure.views[media];
    node = React.createElement(View, __assign({
      name: media
    }, mediaViewProps));
  }

  return React.createElement(NodeStateContext.Provider, {
    value: state
  }, React.createElement(ButtonComponent, __assign({}, isDevelopment && {
    "data-button": "".concat(String(props.name))
  }, prefixWith$(props), mergeProps(props, elementProps || {}), {
    "$styles": styles,
    as: props.as,
    ref: buttonRef,
    sx: sx
  }), ariaLabel && React.createElement(VisuallyHidden, null, ariaLabel), node));
}

Button.defaultProps = {
  buttonRef: undefined
};
/**
 * Renders an input given the current structure context.
 */

function Input(_a) {
  var inputRef = _a.inputRef,
      props = __rest(_a, ["inputRef"]);

  var placeholder = props.placeholder,
      placeholderStyles = props.placeholderStyles,
      valueStyles = props.valueStyles,
      cursorColor = props.cursorColor,
      componentProps = __rest(props, ["placeholder", "placeholderStyles", "valueStyles", "cursorColor"]);

  var placeholderText = useDynamicValue(placeholder);
  return React.createElement(InputComponent, __assign({}, componentProps, prefixWith$({
    placeholderStyles: placeholderStyles,
    valueStyles: valueStyles,
    cursorColor: cursorColor
  }), {
    placeholder: placeholderText,
    style: {
      flex: 1
    },
    ref: inputRef
  }));
}

Input.defaultProps = {
  inputRef: undefined
};
/**
 * Returns a label.
 */

function Label(props) {
  var _a, _b, _c, _d;

  var name = props.name,
      children = props.children;
  var labels = useStructureContext().structure.labels;
  var value = useDynamicValue(name);

  var propAlignment = props.alignment,
      propFrame = props.frame,
      propStyles = props.styles,
      propString = props.string,
      componentProps = __rest(props, ["alignment", "frame", "styles", "string"]);

  var alignment = propAlignment || ((_a = labels === null || labels === void 0 ? void 0 : labels[name]) === null || _a === void 0 ? void 0 : _a.alignment);
  var frame = propFrame || ((_b = labels === null || labels === void 0 ? void 0 : labels[name]) === null || _b === void 0 ? void 0 : _b.frame);
  var styles = propStyles || ((_c = labels === null || labels === void 0 ? void 0 : labels[name]) === null || _c === void 0 ? void 0 : _c.styles);
  var string = propString || ((_d = labels === null || labels === void 0 ? void 0 : labels[name]) === null || _d === void 0 ? void 0 : _d.string);
  return React.createElement(LabelComponent, __assign({}, isDevelopment && {
    "data-label": "".concat(String(name))
  }, componentProps, prefixWith$({
    alignment: alignment,
    frame: frame,
    styles: styles,
    string: string
  })), children || value);
}
/** Returns label text given a name. */


function useDynamicValue(name) {
  var _a, _b;

  var SLOT = "\\()";

  var _c = useStructureContext(),
      labels = _c.structure.labels,
      dynamicValues = _c.dynamicValues;

  var labelValue = labels[name].string;

  if (dynamicValues[name] && labelValue) {
    // If the original label doesn't have a slot,
    // we want to replace it outright.
    if (!labelValue.includes(SLOT)) {
      labelValue = (_a = dynamicValues[name]) === null || _a === void 0 ? void 0 : _a[0];
    } else {
      // Iterate over dynamic values and replace in the string.
      labelValue = ((_b = dynamicValues[name]) === null || _b === void 0 ? void 0 : _b.reduce(function (subject, datum) {
        return subject.replace(SLOT, datum);
      }, labelValue)) || "";
    }
  }

  return labelValue;
}
/**
 * Returns structure values using external structure
 */


export function getStructureValues(dictionary, structure) {
  var values = Object.keys(dictionary).reduce(function (accu, key) {
    var _a, _b, _c;

    var _d, _e, _f, _g, _h, _j, _k, _l;

    var parsedKey = dictionary[key].split(".");
    var structureNodeType = parsedKey[0];

    if (structureNodeType === "buttons") {
      var searchValue = parsedKey[2];
      return __assign((_a = {}, _a[key] = (_h = (_g = (_f = (_e = (_d = structure[structureNodeType]) === null || _d === void 0 ? void 0 : _d[parsedKey[1]]) === null || _e === void 0 ? void 0 : _e.states) === null || _f === void 0 ? void 0 : _f.unselected) === null || _g === void 0 ? void 0 : _g.styles) === null || _h === void 0 ? void 0 : _h[searchValue], _a), accu);
    }

    if (structureNodeType === "labels") {
      var searchValue = parsedKey[2];
      return __assign((_b = {}, _b[key] = (_l = (_k = (_j = structure[structureNodeType]) === null || _j === void 0 ? void 0 : _j[parsedKey[1]]) === null || _k === void 0 ? void 0 : _k.styles) === null || _l === void 0 ? void 0 : _l[searchValue], _b), accu);
    } // TODO: Add support for more dynamic nodes


    return __assign((_c = {}, _c[key] = undefined, _c), accu);
  }, {});
  return values;
}
/**
 * Returns structure values using external structure
 */

export function updateStructureValues(dictionary, structure, values) {
  var construct = structure;
  Object.keys(dictionary).forEach(function (key) {
    if (values[key]) {
      var newValue_1 = String(values[key]);
      dictionary[key].forEach(function (structureKey) {
        var _a, _b, _c, _d;

        var _e, _f;

        var parsedKey = structureKey.split(".");
        var structureNodeType = parsedKey[0]; // Modifying button, not label

        if (structureNodeType === "buttons" && parsedKey[2] !== "label") {
          var isValidColor = chroma.valid(newValue_1);

          var _g = isValidColor ? chroma(newValue_1).hsl() : [0, 0, 0],
              h = _g[0],
              s = _g[1],
              l = _g[2];

          construct[structureNodeType] = merge(structure[structureNodeType], (_a = {}, _a[parsedKey[1]] = {
            states: __assign({
              unselected: {
                styles: (_b = {}, _b[parsedKey[2]] = values[key], _b.stroke = {
                  color: values[key]
                }, _b)
              }
            }, parsedKey[2] === "fillColor" && isValidColor && {
              selected: {
                styles: (_c = {}, _c[parsedKey[2]] = chroma(h, s, l - 0.1, "hsl").hex(), _c.stroke = {
                  color: chroma(h, s, l - 0.1, "hsl").hex()
                }, _c)
              },
              hover: {
                styles: (_d = {}, _d[parsedKey[2]] = chroma(h, s, l + 0.1, "hsl").hex(), _d.stroke = {
                  color: chroma(h, s, l + 0.1, "hsl").hex()
                }, _d)
              }
            })
          }, _a));
        } // Modifiy button's label


        if (structureNodeType === "buttons" && parsedKey[2] === "label") {
          var buttonStates_1 = ((_f = (_e = structure[structureNodeType]) === null || _e === void 0 ? void 0 : _e[parsedKey[1]]) === null || _f === void 0 ? void 0 : _f.states) || {};
          Object.keys(buttonStates_1).forEach(function (state) {
            var _a, _b;

            var _c;

            var stateLabelKey = (_c = buttonStates_1 === null || buttonStates_1 === void 0 ? void 0 : buttonStates_1[state]) === null || _c === void 0 ? void 0 : _c.label;

            if (state === "unselected" || state === "selected" || state === "hover") {
              construct.labels = merge(structure.labels, // Only add if the button state label is defined
              stateLabelKey && (_a = {}, _a[stateLabelKey] = {
                styles: (_b = {}, _b[parsedKey[3]] = values[key], _b)
              }, _a));
            } // TODO: Add more state values automatically using color functions

          });
        }
      });
    }
  });
  return construct;
}
/**
 * Renders a dynamic structure.
 */

function DynamicFactory(_a) {
  var _b = _a.entry,
      entry = _b === void 0 ? "" : _b,
      props = __rest(_a, ["entry"]);

  var ctx = React.useContext(StructureContext);

  var _c = merge({}, ctx, props),
      structure = _c.structure,
      dynamicValues = _c.dynamicValues,
      renderers = _c.renderers,
      merged = __rest(_c, ["structure", "dynamicValues", "renderers"]);

  if (!Object.keys(structure).length || !entry) {
    console.error("lbx/dynamic-web", "Invalid properties provided for the dynamic factory.");
    return null;
  }

  var entryViewAttributes = structure.views["".concat(String(entry))];

  var lookup = __assign({}, merged.lookup);

  Object.entries(structure).forEach(function (_a) {
    var parent = _a[0],
        child = _a[1];
    Object.keys(child).forEach(function (key) {
      lookup[key] = parent;
    });
  });
  var renderer = renderers["".concat(String(entry))] || View;
  return React.createElement(StructureContext.Provider // eslint-disable-next-line
  , {
    // eslint-disable-next-line
    value: {
      structure: structure,
      dynamicValues: dynamicValues,
      renderers: renderers,
      lookup: lookup
    }
  }, typeof renderer === "function" && // TODO: Fix type definitions
  renderer(__assign(__assign({}, entryViewAttributes), {
    name: "".concat(String(entry))
  })));
}

export default DynamicFactory;
export { Button, CollectionView, Input, Label, LinearLayout, View, prefixWith$ };
export { default as defaultDynamicFactoryStructure } from "./structure";
export * from "./types";